import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import toast from 'react-hot-toast';
import { 
  Target, 
  TrendingUp, 
  Search, 
  DollarSign, 
  BookOpen, 
  Star, 
  Copy, 
  CheckCircle, 
  ArrowRight,
  Loader,
  BarChart3,
  Eye,
  Download,
  Sparkles,
  Award,
  Clock,
  RefreshCw,
  X,
  Trash2
} from 'lucide-react';

const MetadataOptimizerPage = () => {
  const { user, API_BASE_URL } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [optimizationResult, setOptimizationResult] = useState(null);
  const [previousOptimizations, setPreviousOptimizations] = useState([]);
  const [selectedOptimization, setSelectedOptimization] = useState(null);
  const [activeTab, setActiveTab] = useState('optimizer');
  const [copiedText, setCopiedText] = useState('');
  const [deletingMetadata, setDeletingMetadata] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [metadataUsage, setMetadataUsage] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    genre: '',
    target_audience: '',
    book_summary: '',
    existing_description: '',
    target_keywords: ''
  });

  useEffect(() => {
    fetchPreviousOptimizations();
    
    // Load saved form data from localStorage first
    const savedFormData = localStorage.getItem('metadataOptimizerFormData');
    if (savedFormData) {
      try {
        const parsedData = JSON.parse(savedFormData);
        setFormData(prev => ({ ...prev, ...parsedData }));
      } catch (error) {
        console.error('Error loading saved form data:', error);
      }
    }
    
    // Pre-fill form if book data is passed from dashboard (this takes priority)
    if (location.state?.bookData) {
      const book = location.state.bookData;
      const newFormData = {
        title: book.title || '',
        genre: book.genre || '',
        target_audience: '',
        book_summary: book.description || '',
        existing_description: book.description || '',
        target_keywords: ''
      };
      setFormData(prev => ({ ...prev, ...newFormData }));
      // Save to localStorage immediately
      localStorage.setItem('metadataOptimizerFormData', JSON.stringify(newFormData));
    }
  }, [location.state]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const fetchPreviousOptimizations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/metadata/optimizations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPreviousOptimizations(data.optimizations);
      }
    } catch (error) {
      console.error('Error fetching optimizations:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const newFormData = {
      ...formData,
      [name]: value
    };
    setFormData(newFormData);
    
    // Save to localStorage for persistence
    localStorage.setItem('metadataOptimizerFormData', JSON.stringify(newFormData));
  };

  const clearForm = () => {
    const emptyFormData = {
      title: '',
      genre: '',
      target_audience: '',
      book_summary: '',
      existing_description: '',
      target_keywords: ''
    };
    setFormData(emptyFormData);
    localStorage.removeItem('metadataOptimizerFormData');
    toast.success('Form cleared!');
  };

  const handleOptimize = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.genre) {
      toast.error('Please provide at least title and genre');
      return;
    }

    setLoading(true);
    toast.loading('Optimizing metadata... This may take a few moments.', { id: 'metadata-loading' });
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/metadata/optimize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: formData.title,
          genre: formData.genre,
          target_audience: formData.target_audience || null,
          book_summary: formData.book_summary || null,
          existing_description: formData.existing_description || null,
          target_keywords: formData.target_keywords ? formData.target_keywords.split(',').map(k => k.trim()) : null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to optimize metadata');
      }

      const data = await response.json();
      setOptimizationResult(data);
      toast.dismiss('metadata-loading');
      toast.success('Metadata optimized successfully!');
      
      // Refresh previous optimizations
      fetchPreviousOptimizations();
      
    } catch (error) {
      console.error('Error optimizing metadata:', error);
      toast.dismiss('metadata-loading');
      toast.error('Failed to optimize metadata. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(label);
      toast.success(`${label} copied to clipboard!`);
      setTimeout(() => setCopiedText(''), 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const checkMetadataUsage = async (metadataId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/metadata/${metadataId}/usage`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        throw new Error('Failed to check metadata usage');
      }
    } catch (error) {
      console.error('Error checking metadata usage:', error);
      toast.error('Failed to check metadata usage');
      return null;
    }
  };

  const deleteMetadata = async (metadata) => {
    setDeletingMetadata(metadata.id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/metadata/optimizations/${metadata.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Metadata deleted successfully!');
        setShowDeleteConfirm(null);
        setMetadataUsage(null);
        // Refresh the metadata list
        fetchPreviousOptimizations();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete metadata');
      }
    } catch (error) {
      console.error('Error deleting metadata:', error);
      toast.error(error.message || 'Failed to delete metadata');
    } finally {
      setDeletingMetadata(null);
    }
  };

  const viewOptimizationDetails = async (optimization) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/metadata/optimizations/${optimization.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const fullOptimization = await response.json();
        setSelectedOptimization(fullOptimization);
      } else {
        console.error('Failed to fetch optimization details');
        toast.error('Failed to load optimization details');
      }
    } catch (error) {
      console.error('Error fetching optimization details:', error);
      toast.error('Failed to load optimization details');
    }
  };

  const confirmDeleteMetadata = async (metadata) => {
    try {
      const usage = await checkMetadataUsage(metadata.id);
      if (usage) {
        setMetadataUsage(usage);
      } else {
        // If usage check fails, proceed with simple confirmation
        setMetadataUsage({ is_in_use: false, books_count: 0, message: "Ready to delete" });
      }
      setShowDeleteConfirm(metadata);
    } catch (error) {
      console.error('Error in confirmDeleteMetadata:', error);
      // If usage check fails entirely, still allow deletion with warning
      setMetadataUsage({ is_in_use: false, books_count: 0, message: "Usage check failed - proceed with caution" });
      setShowDeleteConfirm(metadata);
    }
  };

  const OptimizationDetailsModal = ({ optimization, onClose }) => {
    if (!optimization) return null;

    const result = optimization.optimization_result;
    const request = optimization.original_request;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-gray-900 rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
          <div className="p-6 border-b border-gray-700 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">{request?.title || 'Metadata Optimization'}</h2>
              <p className="text-gray-400 mt-1">
                {request?.genre} • {new Date(optimization.created_at).toLocaleDateString()}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            <div className="space-y-8">
              
              {/* SEO Score */}
              <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-lg p-6 border border-purple-500/20">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">SEO Score</h3>
                  <div className="text-3xl font-bold text-purple-400">
                    {result?.seo_score || 0}/10
                  </div>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full"
                    style={{ width: `${((result?.seo_score || 0) / 10) * 100}%` }}
                  />
                </div>
              </div>

              {/* Optimized Title */}
              <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-600">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-white">Optimized Title</h3>
                  <button
                    onClick={() => copyToClipboard(result?.optimized_title || '', 'title')}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {copiedText === 'title' ? <CheckCircle className="h-5 w-5 text-green-400" /> : <Copy className="h-5 w-5" />}
                  </button>
                </div>
                <p className="text-white text-lg leading-relaxed">
                  {result?.optimized_title || 'No optimized title available'}
                </p>
                {result?.optimized_subtitle && (
                  <p className="text-gray-400 text-sm mt-2">
                    Subtitle: {result.optimized_subtitle}
                  </p>
                )}
              </div>

              {/* Amazon Description */}
              <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-600">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-white">Amazon Description</h3>
                  <button
                    onClick={() => copyToClipboard(result?.amazon_description || '', 'description')}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {copiedText === 'description' ? <CheckCircle className="h-5 w-5 text-green-400" /> : <Copy className="h-5 w-5" />}
                  </button>
                </div>
                <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">
                  {result?.amazon_description || 'No description available'}
                </p>
              </div>

              {/* Keywords */}
              <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-600">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-white">Recommended Keywords</h3>
                  <button
                    onClick={() => copyToClipboard(
                      result?.recommended_keywords?.map(k => typeof k === 'string' ? k : k.keyword).join(', ') || '', 
                      'keywords'
                    )}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {copiedText === 'keywords' ? <CheckCircle className="h-5 w-5 text-green-400" /> : <Copy className="h-5 w-5" />}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {result?.recommended_keywords?.map((keyword, idx) => (
                    <span 
                      key={idx} 
                      className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full text-sm"
                    >
                      {typeof keyword === 'string' ? keyword : keyword.keyword}
                    </span>
                  ))}
                </div>
              </div>

              {/* Categories */}
              <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-600">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-white">Category Suggestions</h3>
                  <button
                    onClick={() => copyToClipboard(
                      result?.category_suggestions?.map(c => typeof c === 'string' ? c : c.category).join(', ') || '', 
                      'categories'
                    )}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {copiedText === 'categories' ? <CheckCircle className="h-5 w-5 text-green-400" /> : <Copy className="h-5 w-5" />}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {result?.category_suggestions?.map((category, idx) => (
                    <span 
                      key={idx} 
                      className="bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full text-sm"
                    >
                      {typeof category === 'string' ? category : category.category}
                    </span>
                  ))}
                </div>
              </div>

              {/* Pricing Suggestions */}
              {result?.pricing_suggestions && (
                <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-600">
                  <h3 className="text-lg font-semibold text-white mb-3">Pricing Suggestions</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {result.pricing_suggestions.map((price, idx) => (
                      <div key={idx} className="bg-gray-700/50 rounded-lg p-4">
                        <div className="text-2xl font-bold text-green-400 mb-2">
                          ${price.price}
                        </div>
                        <p className="text-gray-300 text-sm">
                          {price.rationale}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Competition Analysis */}
              {result?.competition_analysis && (
                <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-600">
                  <h3 className="text-lg font-semibold text-white mb-3">Competition Analysis</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Market Saturation:</span>
                      <span className="text-white">{result.competition_analysis.market_saturation}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Opportunity Score:</span>
                      <span className="text-green-400">{result.competition_analysis.opportunity_score}/10</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Recommended Focus:</span>
                      <span className="text-white">{result.competition_analysis.recommended_focus}</span>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderKeywordAnalysis = (keywords) => {
    if (!keywords || keywords.length === 0) return <p className="text-gray-400">No keywords available</p>;
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {keywords.map((keyword, index) => (
          <div key={index} className="bg-gray-800/50 rounded-lg p-4 border border-purple-500/20">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-white">{keyword.keyword}</h4>
              <span className={`px-2 py-1 text-xs rounded-full ${
                keyword.search_difficulty === 'Low' ? 'bg-green-500/20 text-green-300' :
                keyword.search_difficulty === 'Medium' ? 'bg-yellow-500/20 text-yellow-300' :
                'bg-red-500/20 text-red-300'
              }`}>
                {keyword.search_difficulty}
              </span>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Relevance:</span>
                <div className="flex items-center space-x-1">
                  <Star className="h-4 w-4 text-yellow-400" />
                  <span className="text-white">{keyword.relevance_score}/10</span>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Monthly Searches:</span>
                <span className="text-white">{keyword.monthly_searches?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Category Match:</span>
                <span className="text-white">{keyword.category_match}/10</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderOptimizationResult = () => {
    if (!optimizationResult) return null;

    return (
      <div className="space-y-8">
        {/* SEO Score */}
        <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-2xl p-6 border border-purple-500/30">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-2xl font-bold text-white">SEO Optimization Score</h3>
            <div className="flex items-center space-x-2">
              <Award className="h-8 w-8 text-yellow-400" />
              <span className="text-3xl font-bold text-white">{optimizationResult.seo_score}/10</span>
            </div>
          </div>
          
          <div className="bg-gray-800/50 rounded-full h-3 mb-4">
            <div 
              className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${optimizationResult.seo_score * 10}%` }}
            />
          </div>
          
          {optimizationResult.optimization_tips && (
            <div className="mt-4">
              <h4 className="font-semibold text-white mb-2">Optimization Tips:</h4>
              <ul className="space-y-1">
                {optimizationResult.optimization_tips.map((tip, index) => (
                  <li key={index} className="text-gray-300 text-sm flex items-start space-x-2">
                    <ArrowRight className="h-4 w-4 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Title & Subtitle */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-900/50 rounded-2xl p-6 border border-purple-500/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Optimized Title</h3>
              <button
                onClick={() => copyToClipboard(optimizationResult.optimized_title, 'Title')}
                className="text-purple-400 hover:text-purple-300 transition-colors"
              >
                {copiedText === 'Title' ? <CheckCircle className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
              </button>
            </div>
            <p className="text-gray-300 leading-relaxed">{optimizationResult.optimized_title}</p>
          </div>

          <div className="bg-gray-900/50 rounded-2xl p-6 border border-purple-500/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Optimized Subtitle</h3>
              <button
                onClick={() => copyToClipboard(optimizationResult.optimized_subtitle, 'Subtitle')}
                className="text-purple-400 hover:text-purple-300 transition-colors"
              >
                {copiedText === 'Subtitle' ? <CheckCircle className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
              </button>
            </div>
            <p className="text-gray-300 leading-relaxed">{optimizationResult.optimized_subtitle}</p>
          </div>
        </div>

        {/* Descriptions */}
        <div className="space-y-6">
          <div className="bg-gray-900/50 rounded-2xl p-6 border border-purple-500/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Amazon Description</h3>
              <button
                onClick={() => copyToClipboard(optimizationResult.amazon_description, 'Amazon Description')}
                className="text-purple-400 hover:text-purple-300 transition-colors"
              >
                {copiedText === 'Amazon Description' ? <CheckCircle className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
              </button>
            </div>
            <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{optimizationResult.amazon_description}</p>
          </div>

          <div className="bg-gray-900/50 rounded-2xl p-6 border border-purple-500/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">SEO Description</h3>
              <button
                onClick={() => copyToClipboard(optimizationResult.seo_description, 'SEO Description')}
                className="text-purple-400 hover:text-purple-300 transition-colors"
              >
                {copiedText === 'SEO Description' ? <CheckCircle className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
              </button>
            </div>
            <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{optimizationResult.seo_description}</p>
          </div>

          <div className="bg-gray-900/50 rounded-2xl p-6 border border-purple-500/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Back Cover Text</h3>
              <button
                onClick={() => copyToClipboard(optimizationResult.back_cover_text, 'Back Cover Text')}
                className="text-purple-400 hover:text-purple-300 transition-colors"
              >
                {copiedText === 'Back Cover Text' ? <CheckCircle className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
              </button>
            </div>
            <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{optimizationResult.back_cover_text}</p>
          </div>
        </div>

        {/* Keywords */}
        <div className="bg-gray-900/50 rounded-2xl p-6 border border-purple-500/20">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
              <Search className="h-5 w-5 text-purple-400" />
              <span>Recommended Keywords</span>
            </h3>
          </div>
          {renderKeywordAnalysis(optimizationResult.recommended_keywords)}
        </div>

        {/* Categories */}
        {optimizationResult.category_suggestions && optimizationResult.category_suggestions.length > 0 && (
          <div className="bg-gray-900/50 rounded-2xl p-6 border border-purple-500/20">
            <h3 className="text-lg font-semibold text-white mb-6 flex items-center space-x-2">
              <BarChart3 className="h-5 w-5 text-purple-400" />
              <span>Category Recommendations</span>
            </h3>
            <div className="space-y-4">
              {optimizationResult.category_suggestions.map((category, index) => (
                <div key={index} className="bg-gray-800/50 rounded-lg p-4 border border-purple-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-white">{category.category}</h4>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      category.competition_level === 'Low' ? 'bg-green-500/20 text-green-300' :
                      category.competition_level === 'Medium' ? 'bg-yellow-500/20 text-yellow-300' :
                      'bg-red-500/20 text-red-300'
                    }`}>
                      {category.competition_level} Competition
                    </span>
                  </div>
                  <div className="flex items-center space-x-4 mb-2">
                    <div className="flex items-center space-x-1">
                      <TrendingUp className="h-4 w-4 text-purple-400" />
                      <span className="text-sm text-gray-300">Score: {category.opportunity_score}/10</span>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm">{category.why_recommended}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pricing */}
        {optimizationResult.pricing_recommendations && (
          <div className="bg-gray-900/50 rounded-2xl p-6 border border-purple-500/20">
            <h3 className="text-lg font-semibold text-white mb-6 flex items-center space-x-2">
              <DollarSign className="h-5 w-5 text-purple-400" />
              <span>Pricing Strategy</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                <h4 className="font-semibold text-white mb-2">Launch Price</h4>
                <p className="text-2xl font-bold text-green-400">
                  ${optimizationResult.pricing_recommendations.launch_price}
                </p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                <h4 className="font-semibold text-white mb-2">Promotional</h4>
                <p className="text-2xl font-bold text-purple-400">
                  ${optimizationResult.pricing_recommendations.promotional_price}
                </p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                <h4 className="font-semibold text-white mb-2">Optimal Range</h4>
                <p className="text-lg font-bold text-blue-400">
                  ${optimizationResult.pricing_recommendations.optimal_range}
                </p>
              </div>
            </div>
            {optimizationResult.pricing_recommendations.reasoning && (
              <div className="mt-4 p-4 bg-gray-800/30 rounded-lg">
                <p className="text-gray-300 text-sm">{optimizationResult.pricing_recommendations.reasoning}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <SEOHead 
        title="KDP Metadata Optimizer - AI-Powered Book SEO & Keywords"
        description="Optimize your book's metadata for maximum discoverability on Amazon KDP. AI-powered title, description, and keyword optimization for better rankings and sales."
        keywords="KDP metadata optimizer, book SEO, Amazon book keywords, book title optimization, book description optimizer, KDP SEO tool, book metadata generator"
        canonical="/metadata-optimizer"
        ogTitle="KDP Metadata Optimizer - Boost Your Book's Discoverability"
        ogDescription="AI-powered metadata optimization for Amazon KDP. Get optimized titles, descriptions, and keywords to increase your book sales."
        ogImage="/metadata-optimization.png"
        ogUrl="/metadata-optimizer"
        schemaData={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "KDP Metadata Optimizer",
          "description": "AI-powered tool for optimizing Amazon KDP book metadata and SEO",
          "applicationCategory": "SEOApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "description": "Free trial with credits"
          }
        }}
      />

      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900">
        <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2 flex items-center space-x-3">
            <Target className="h-10 w-10 text-purple-400" />
            <span>KDP Metadata Optimizer</span>
          </h1>
          <p className="text-gray-300">
            AI-powered metadata optimization for maximum book discoverability and sales
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="flex space-x-1 bg-gray-900/50 p-1 rounded-2xl border border-purple-500/20">
            <button
              onClick={() => setActiveTab('optimizer')}
              className={`px-6 py-3 rounded-xl font-medium transition-all ${
                activeTab === 'optimizer'
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Sparkles className="h-5 w-5 inline mr-2" />
              Optimizer
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-6 py-3 rounded-xl font-medium transition-all ${
                activeTab === 'history'
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Clock className="h-5 w-5 inline mr-2" />
              History ({previousOptimizations.length})
            </button>
          </div>
        </div>

        {activeTab === 'optimizer' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Optimization Form */}
            <div className="lg:col-span-1">
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20 sticky top-24">
                <h2 className="text-xl font-bold text-white mb-6">Book Information</h2>
                
                <form onSubmit={handleOptimize} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Book Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Enter your book title"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Genre *
                    </label>
                    <select
                      name="genre"
                      value={formData.genre}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select Genre</option>
                      <option value="Romance">Romance</option>
                      <option value="Thriller">Thriller</option>
                      <option value="Fantasy">Fantasy</option>
                      <option value="Sci-Fi">Science Fiction</option>
                      <option value="Mystery">Mystery</option>
                      <option value="Horror">Horror</option>
                      <option value="Business">Business</option>
                      <option value="Self-Help">Self-Help</option>
                      <option value="Non-Fiction">Non-Fiction</option>
                      <option value="Biography">Biography</option>
                      <option value="Children">Children's</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Target Audience
                    </label>
                    <input
                      type="text"
                      name="target_audience"
                      value={formData.target_audience}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="e.g., Young adults, Business professionals"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Book Summary
                    </label>
                    <textarea
                      name="book_summary"
                      value={formData.book_summary}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Brief summary of your book's content and main themes"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Existing Description
                    </label>
                    <textarea
                      name="existing_description"
                      value={formData.existing_description}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Current book description (if any)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Target Keywords
                    </label>
                    <input
                      type="text"
                      name="target_keywords"
                      value={formData.target_keywords}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="keyword1, keyword2, keyword3"
                    />
                    <p className="text-xs text-gray-400 mt-1">Separate keywords with commas</p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      {loading ? (
                        <>
                          <Loader className="h-5 w-5 animate-spin" />
                          <span>Optimizing...</span>
                        </>
                      ) : (
                        <>
                          <Target className="h-5 w-5" />
                          <span>Optimize Metadata</span>
                        </>
                      )}
                    </button>
                    
                    <button
                      type="button"
                      onClick={clearForm}
                      disabled={loading}
                      className="px-4 py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      title="Clear Form"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Results */}
            <div className="lg:col-span-2">
              {optimizationResult ? (
                renderOptimizationResult()
              ) : (
                <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-12 border border-purple-500/20 text-center">
                  <Target className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-400 mb-2">
                    Ready to Optimize
                  </h3>
                  <p className="text-gray-500">
                    Fill in your book details and click "Optimize Metadata" to get AI-powered recommendations
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-6">
            {previousOptimizations.length === 0 ? (
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-12 border border-purple-500/20 text-center">
                <Clock className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-400 mb-2">
                  No Optimizations Yet
                </h3>
                <p className="text-gray-500 mb-6">
                  Start optimizing your book metadata to see your history here
                </p>
                <button
                  onClick={() => setActiveTab('optimizer')}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-3 rounded-full font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200"
                >
                  Start Optimizing
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {previousOptimizations.map((optimization) => (
                  <div
                    key={optimization.id}
                    className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20 hover:border-purple-400/40 transition-all duration-200"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-lg font-semibold text-white truncate mr-2">
                        {optimization.title}
                      </h3>
                      <div className="flex items-center space-x-1">
                        <Star className="h-4 w-4 text-yellow-400" />
                        <span className="text-sm text-white">{optimization.seo_score}/10</span>
                      </div>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <p className="text-gray-300 text-sm">
                        <span className="font-medium">Genre:</span> {optimization.genre}
                      </p>
                      <p className="text-gray-300 text-sm">
                        <span className="font-medium">Provider:</span> {optimization.provider}
                      </p>
                      <p className="text-gray-300 text-sm">
                        <span className="font-medium">Created:</span>{' '}
                        {new Date(optimization.created_at).toLocaleDateString()}
                      </p>
                    </div>

                    <div className="flex space-x-2">
                      <button 
                        onClick={() => viewOptimizationDetails(optimization)}
                        className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2"
                      >
                        <Eye className="h-4 w-4" />
                        <span>View Details</span>
                      </button>
                      
                      <button
                        onClick={() => confirmDeleteMetadata(optimization)}
                        className="bg-red-600 text-white p-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center"
                        title="Delete Metadata"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Details Modal */}
            <OptimizationDetailsModal
              optimization={selectedOptimization}
              onClose={() => setSelectedOptimization(null)}
            />

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-gray-900 rounded-2xl max-w-md w-full">
                  <div className="p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="bg-red-500/20 p-3 rounded-full">
                        <Trash2 className="h-6 w-6 text-red-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">Delete Metadata</h3>
                        <p className="text-gray-400 text-sm">This action cannot be undone</p>
                      </div>
                    </div>
                    
                    <div className="mb-6">
                      <p className="text-gray-300 mb-3">
                        Are you sure you want to delete the metadata optimization for "{showDeleteConfirm.title}"?
                      </p>
                      
                      {metadataUsage && metadataUsage.is_in_use && (
                        <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4 mb-4">
                          <div className="flex items-center space-x-2 mb-2">
                            <Award className="h-4 w-4 text-yellow-400" />
                            <span className="text-yellow-300 font-medium text-sm">Metadata In Use</span>
                          </div>
                          <p className="text-yellow-200 text-sm mb-2">
                            This metadata is currently being used by {metadataUsage.books.length} book(s):
                          </p>
                          <ul className="list-disc list-inside text-yellow-200 text-sm">
                            {metadataUsage.books.map((book, idx) => (
                              <li key={idx}>{book.title}</li>
                            ))}
                          </ul>
                          <p className="text-yellow-300 text-xs mt-2">
                            Deleting this metadata will remove it from these books.
                          </p>
                        </div>
                      )}
                      
                      {metadataUsage && !metadataUsage.is_in_use && (
                        <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-3 mb-4">
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="h-4 w-4 text-green-400" />
                            <span className="text-green-300 text-sm">Safe to delete - not currently in use</span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex space-x-3">
                      <button
                        onClick={() => {
                          setShowDeleteConfirm(null);
                          setMetadataUsage(null);
                        }}
                        className="flex-1 bg-gray-700 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-600 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => deleteMetadata(showDeleteConfirm)}
                        disabled={deletingMetadata === showDeleteConfirm.id}
                        className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        {deletingMetadata === showDeleteConfirm.id ? (
                          <>
                            <Loader className="h-4 w-4 animate-spin mr-2" />
                            Deleting...
                          </>
                        ) : (
                          metadataUsage?.is_in_use ? 'Delete Anyway' : 'Delete Metadata'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default MetadataOptimizerPage;